// src/components/Header.js
import React, { useState, useEffect, useRef } from 'react';
import { routes } from '../routes';
import { supabase } from '../supabaseClient';

const Header = ({ onNavigate, cartItemCount, onSearch, user, onLogout }) => {
  // Menú hamburguesa (móvil)
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Búsqueda y sugerencias
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false); // solo desktop
  const [suggestions, setSuggestions] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const searchInputRef = useRef(null);

  // Menús de usuario (personita): refs separados para móvil y desktop
  const [openUserMenu, setOpenUserMenu] = useState(false);
  const userMenuRefMobile = useRef(null);
  const userMenuRefDesktop = useRef(null);

  const currentPath = window.location.pathname;

  // Cargar productos para sugerencias
  useEffect(() => {
    (async () => {
      const { data, error } = await supabase.from('products').select('*');
      if (error) console.error('Error al obtener productos:', error);
      setAllProducts(data || []);
    })();
  }, []);

  // Filtrar sugerencias
  useEffect(() => {
    if (!searchTerm.trim()) return setSuggestions([]);
    const q = searchTerm.toLowerCase();
    setSuggestions(allProducts.filter(p => p.name.toLowerCase().includes(q)).slice(0, 5));
  }, [searchTerm, allProducts]);

  // Cerrar dropdown usuario si click-fuera (usar 'click', NO 'mousedown'), considerando AMBOS refs
  useEffect(() => {
    const onDocClick = (e) => {
      const insideMobile = userMenuRefMobile.current?.contains(e.target);
      const insideDesktop = userMenuRefDesktop.current?.contains(e.target);
      if (!insideMobile && !insideDesktop) setOpenUserMenu(false);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  // Navegación centralizada
  const handleNavLinkClick = (path) => {
    onNavigate(path);
    setIsMenuOpen(false);       // cierra menú hamburguesa si estaba abierto
    setOpenUserMenu(false);     // cierra dropdown usuario
    setIsSearchExpanded(false); // cierra búsqueda desktop
    setSuggestions([]);
  };

  // Buscar
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) onSearch(searchTerm.trim());
    setIsSearchExpanded(false);
    setIsMenuOpen(false);
    setSuggestions([]);
  };

  const toggleSearch = () => {
    setIsSearchExpanded((prev) => {
      if (!prev) setTimeout(() => searchInputRef.current?.focus(), 300);
      else setSuggestions([]);
      return !prev;
    });
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSuggestions([]);
    onSearch('');
    searchInputRef.current?.focus();
  };

  const handleSuggestionClick = (productName) => {
    setSearchTerm(productName);
    onSearch(productName);
    setIsSearchExpanded(false);
    setSuggestions([]);
    setIsMenuOpen(false);
  };

  const getNavLinkClass = (path) =>
    `text-base lg:text-lg font-medium transition-colors ${
      currentPath === path ? 'text-[#5A61E5]' : 'text-gray-700 hover:text-gray-900'
    }`;

  return (
    <header className="bg-white bg-opacity-90 backdrop-blur-md shadow-md sticky top-0 z-50 p-4 sm:p-6">
      <div className="container mx-auto flex flex-col">

        {/* ====== Mobile Header ====== */}
        <div className="md:hidden flex items-center justify-between w-full">
          {/* IZQUIERDA: HAMBURGUESA */}
          <div className="w-1/3 flex justify-start">
            <button
              type="button"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-gray-900 focus:outline-none"
              aria-label="Abrir menú"
            >
              <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* CENTRO: LOGO */}
          <div className="w-1/3 flex justify-center">
            <div className="cursor-pointer" onClick={() => handleNavLinkClick(routes.home)}>
              <img
                src="https://jszgeoosgpozjlvohrjy.supabase.co/storage/v1/object/public/assets//logo%20morado.png"
                alt="El Ancla CR Logo"
                className="h-12"
              />
            </div>
          </div>

          {/* DERECHA: 🛒 primero, luego 👤 */}
          <div className="w-1/3 flex justify-end items-center space-x-4">
            {/* Carrito */}
            <button
              type="button"
              onClick={() => handleNavLinkClick(routes.cart)}
              className="relative text-[#5A61E5] hover:text-[#4A51D5] focus:outline-none"
              aria-label="Carrito"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              {cartItemCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#F2BF53] text-gray-900 text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {cartItemCount}
                </span>
              )}
            </button>

            {/* Personita (MÓVIL): botón + dropdown (usar ref móvil) */}
            <div className="relative" ref={userMenuRefMobile}>
              <button
                type="button"
                onClick={() => setOpenUserMenu(v => !v)}
                className="text-[#5A61E5] hover:text-[#4A51D5] focus:outline-none"
                aria-haspopup="menu"
                aria-expanded={openUserMenu}
                aria-label="Menú de usuario"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M15.75 7.5a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.5 19.5a7.5 7.5 0 0115 0" />
                </svg>
              </button>

              {openUserMenu && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-56 bg-white border rounded-xl shadow-lg p-2 z-50"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  {user ? (
                    <>
                      <div className="px-3 py-2 text-sm text-gray-600">
                        <div className="font-semibold truncate">{user.nombre}</div>
                        <div className="truncate">{user.correo}</div>
                      </div>
                      <hr className="my-1" />

                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => handleNavLinkClick(routes.misPedidos)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-sm"
                      >
                        Mis pedidos
                      </button>

                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => handleNavLinkClick(routes.miCuenta)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-sm"
                      >
                        Mi cuenta
                      </button>

                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => { onLogout?.(); setOpenUserMenu(false); }}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-sm text-red-600"
                      >
                        Cerrar sesión
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => handleNavLinkClick(routes.login)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-sm"
                      >
                        Iniciar sesión
                      </button>
                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => handleNavLinkClick(routes.registro)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-sm"
                      >
                        Registrarme
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ====== Desktop Header ====== */}
        <div className="hidden md:flex items-center justify-center relative w-full h-20">
          {/* Logo centrado */}
          <div className="absolute left-1/2 transform -translate-x-1/2 cursor-pointer" onClick={() => handleNavLinkClick(routes.home)}>
            <img
              src="https://jszgeoosgpozjlvohrjy.supabase.co/storage/v1/object/public/assets//logo%20morado.png"
              alt="El Ancla CR Logo"
              className="h-16 sm:h-20"
            />
          </div>

          {/* Iconos derecha */}
          <div className="absolute right-4 flex items-center space-x-4">
            {/* Búsqueda */}
            <div className="relative flex items-center">
              <button
                type="button"
                onClick={toggleSearch}
                className="text-[#5A61E5] hover:text-[#4A51D5] focus:outline-none p-2 rounded-full transition-colors duration-300 z-10"
                aria-label="Buscar"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
              <form
                onSubmit={handleSearchSubmit}
                className={`absolute right-0 transition-all duration-300 ease-in-out overflow-hidden flex items-center ${isSearchExpanded ? 'w-64 opacity-100' : 'w-0 opacity-0'}`}
              >
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Buscar productos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-l-xl focus:outline-none focus:ring-2 focus:ring-[#5A61E5] ${isSearchExpanded ? 'block' : 'hidden'}`}
                />
                {isSearchExpanded && searchTerm && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="bg-gray-200 text-gray-600 px-3 py-2 rounded-r-xl hover:bg-gray-300 transition-colors -ml-1 z-20"
                    aria-label="Limpiar búsqueda"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </form>
              {isSearchExpanded && suggestions.length > 0 && (
                <div className="absolute top-full right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-30">
                  {suggestions.map((product) => (
                    <button
                      key={product.id}
                      type="button"
                      onClick={() => handleSuggestionClick(product.name)}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors text-gray-800"
                    >
                      {product.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Menú usuario (DESKTOP): usar ref desktop */}
            <div className="relative" ref={userMenuRefDesktop}>
              <button
                type="button"
                onClick={() => setOpenUserMenu(v => !v)}
                className="flex items-center gap-2 px-3 py-2 rounded-full hover:bg-gray-100 transition"
                aria-haspopup="menu"
                aria-expanded={openUserMenu}
                aria-label="Menú de usuario"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#5A61E5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                        d="M15.75 7.5a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.5 19.5a7.5 7.5 0 0115 0" />
                </svg>
              </button>

              {openUserMenu && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-56 bg-white border rounded-xl shadow-lg p-2 z-50"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  {user ? (
                    <>
                      <div className="px-3 py-2 text-sm text-gray-600">
                        <div className="font-semibold truncate">{user.nombre}</div>
                        <div className="truncate">{user.correo}</div>
                      </div>
                      <hr className="my-1" />

                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => handleNavLinkClick(routes.misPedidos)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-sm"
                      >
                        Mis pedidos
                      </button>

                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => handleNavLinkClick(routes.miCuenta)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-sm"
                      >
                        Mi cuenta
                      </button>

                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => { onLogout?.(); setOpenUserMenu(false); }}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-sm text-red-600"
                      >
                        Cerrar sesión
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => handleNavLinkClick(routes.login)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-sm"
                      >
                        Iniciar sesión
                      </button>

                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => handleNavLinkClick(routes.registro)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-sm"
                      >
                        Registrarme
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Carrito (desktop) */}
            <button
              type="button"
              onClick={() => handleNavLinkClick(routes.cart)}
              className="relative text-[#5A61E5] hover:text-[#4A51D5] text-base lg:text-lg font-medium transition-colors"
              aria-label="Carrito"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              {cartItemCount > 0 && (
                <span className="absolute -top-2 -right-4 bg-[#F2BF53] text-gray-900 text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {cartItemCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* ====== Navigation Menu Desktop ====== */}
        <nav className="hidden md:flex justify-center w-full mt-4">
          <div className="flex space-x-8 lg:space-x-12">
            <button type="button" onClick={() => handleNavLinkClick(routes.home)} className={getNavLinkClass(routes.home)}>Inicio</button>
            <button type="button" onClick={() => handleNavLinkClick(routes.perfumes)} className={getNavLinkClass(routes.perfumes)}>Perfumes</button>
            <button type="button" onClick={() => handleNavLinkClick(routes.tecnologia)} className={getNavLinkClass(routes.tecnologia)}>Tecnología</button>
          </div>
        </nav>

        {/* ====== Mobile Menu (simple, colapsable) ====== */}
        {isMenuOpen && (
          <div className="md:hidden bg-white py-4 shadow-lg">
            <form onSubmit={handleSearchSubmit} className="flex px-4 mb-4">
              <input
                type="text"
                placeholder="Buscar productos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-l-xl focus:outline-none focus:ring-2 focus:ring-[#5A61E5]"
              />
              <button type="submit" className="bg-[#5A61E5] text-white px-4 py-2 rounded-r-xl hover:bg-[#4A51D5] transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </form>

            {suggestions.length > 0 && (
              <div className="px-4 pb-2">
                {suggestions.map((product) => (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => handleSuggestionClick(product.name)}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors text-gray-800 border-b border-gray-100 last:border-b-0"
                  >
                    {product.name}
                  </button>
                ))}
              </div>
            )}

            <nav className="flex flex-col items-center space-y-4">
              <button type="button" onClick={() => handleNavLinkClick(routes.home)} className={getNavLinkClass(routes.home)}>Inicio</button>
              <button type="button" onClick={() => handleNavLinkClick(routes.perfumes)} className={getNavLinkClass(routes.perfumes)}>Perfumes</button>
              <button type="button" onClick={() => handleNavLinkClick(routes.tecnologia)} className={getNavLinkClass(routes.tecnologia)}>Tecnología</button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
